import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { API_BASE_URL } from "@/config";
import { getAccessToken } from "@/lib/auth-utils";
import { useDespachos } from "@/hooks/useDespachos";
import { resolveNotificationDestination } from "./NotificationBell";
import {
  Bell,
  Check,
  Trash2,
  Filter,
  Search,
  FileText,
  AlertCircle,
  MessageSquare,
  TrendingUp,
  BookOpen,
  FileCheck,
  Shield,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { useNotifications } from "@/hooks/useNotifications";
import { useUser } from "@/hooks/useUser";

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { processes } = useDespachos();
  const {
    notifications,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [prefs, setPrefs] = useState({
    email_notifications: true,
    push_notifications: true,
    case_updates: true,
  });

  useEffect(() => {
    if (!user) return;
    supabase
      .from("notification_preferences")
      .select("email_notifications, push_notifications, case_updates")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setPrefs({
            email_notifications: data.email_notifications ?? true,
            push_notifications: data.push_notifications ?? true,
            case_updates: data.case_updates ?? true,
          });
        }
      });
  }, [user]);

  const toggleQuickPref = async (key: "email_notifications" | "push_notifications" | "case_updates") => {
    if (!user) return;
    const nextVal = !prefs[key];
    setPrefs(p => ({ ...p, [key]: nextVal }));
    try {
      const { error: sbError } = await supabase
        .from("notification_preferences")
        .upsert({ user_id: user.id, [key]: nextVal, updated_at: new Date().toISOString() }, { onConflict: "user_id" });
      if (sbError) {
        const token = await getAccessToken();
        const res = await fetch(`${API_BASE_URL}/api/v1/auth/notification-preferences`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ [key]: nextVal })
        });
        if (!res.ok) throw sbError;
      }
      toast.success("Preferencia actualizada.");
    } catch (e) {
      setPrefs(p => ({ ...p, [key]: !nextVal }));
      toast.error("Error al guardar preferencia.");
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "document":
        return <FileText className="h-5 w-5 text-legal-blue" />;
      case "alert":
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case "consultation":
        return <MessageSquare className="h-5 w-5 text-green-500" />;
      case "deadline":
        return <TrendingUp className="h-5 w-5 text-red-500" />;
      case "patent":
        return <BookOpen className="h-5 w-5 text-purple-500" />;
      case "compliance":
        return <Shield className="h-5 w-5 text-amber-500" />;
      case "connection":
        return <Users className="h-5 w-5 text-blue-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  // Filter notifications based on active tab and search query
  const filteredNotifications = notifications.filter((notification) => {
    const matchesTab =
      activeTab === "all" ||
      (activeTab === "unread" && !notification.read) ||
      (activeTab === "read" && notification.read);

    const matchesSearch =
      searchQuery === "" ||
      notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      notification.description
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());

    return matchesTab && matchesSearch;
  });

  // Get notification badge based on type
  const getNotificationBadge = (type: string) => {
    switch (type) {
      case "document":
        return (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-200">
            Documento
          </Badge>
        );
      case "alert":
        return (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200">
            Alerta
          </Badge>
        );
      case "consultation":
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200">
            Consulta
          </Badge>
        );
      case "deadline":
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-200">
            Vencimiento
          </Badge>
        );
      case "patent":
        return (
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-200">
            Patente
          </Badge>
        );
      case "compliance":
        return (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200">
            Cumplimiento
          </Badge>
        );
      case "connection":
        return (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-200">
            Conexión
          </Badge>
        );
      default:
        return <Badge variant="outline">Notificación</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-legal-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 py-8">
        Error al cargar las notificaciones
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-legal-navy">Notificaciones</h2>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => markAllAsRead()}>
            <Check className="h-4 w-4 mr-2" />
            Marcar todas como leídas
          </Button>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="search"
                placeholder="Buscar notificaciones..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Tabs
              defaultValue="all"
              className="w-[400px]"
              onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all">Todas</TabsTrigger>
                <TabsTrigger value="unread">No leídas</TabsTrigger>
                <TabsTrigger value="read">Leídas</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                No hay notificaciones que coincidan con tu búsqueda
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => {
                const dest = resolveNotificationDestination(notification, processes);
                return (
                  <div
                    key={notification.id}
                    className={`flex items-start p-4 rounded-lg border transition-colors ${
                      dest.isCase ? "hover:border-blue-300 hover:bg-blue-50/20 cursor-pointer" : ""
                    } ${
                      notification.read
                        ? "bg-white border-gray-200"
                        : "bg-blue-50/40 border-blue-200"
                    }`}
                    onClick={() => {
                      if (!notification.read) markAsRead(notification.id);
                      if (dest.isCase) {
                        navigate(dest.url);
                      }
                    }}
                  >
                    <div className="mr-4 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3
                            className={`font-medium ${
                              notification.read
                                ? "text-gray-900"
                                : "text-legal-navy"
                            }`}>
                            {notification.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {notification.description}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {dest.isCase && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs border-blue-200 text-legal-blue hover:bg-blue-50 font-medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!notification.read) markAsRead(notification.id);
                                navigate(dest.url);
                              }}
                            >
                              Ver Expediente
                            </Button>
                          )}
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-legal-blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}>
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-500 hover:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-2">
                          {getNotificationBadge(notification.type)}
                          {dest.radicado && (
                            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-700 font-mono">
                              Rad: {dest.radicado}
                            </Badge>
                          )}
                          <span className="text-xs text-gray-500">
                            {format(
                              parseISO(notification.created_at),
                              "d 'de' MMMM, HH:mm",
                              {
                                locale: es,
                              }
                            )}
                          </span>
                        </div>
                        {!notification.read && (
                          <Badge
                            variant="secondary"
                            className="bg-legal-gold text-white">
                            Nuevo
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Resumen de Notificaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Total</span>
                <span className="text-sm font-bold">
                  {notifications.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">No leídas</span>
                <span className="text-sm font-bold text-legal-blue">
                  {notifications.filter((n) => !n.read).length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Leídas</span>
                <span className="text-sm font-bold text-gray-500">
                  {notifications.filter((n) => n.read).length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-legal-blue" />
                  Documentos
                </span>
                <span className="text-sm font-bold">
                  {notifications.filter((n) => n.type === "document").length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 text-amber-500" />
                  Alertas
                </span>
                <span className="text-sm font-bold">
                  {notifications.filter((n) => n.type === "alert").length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2 text-green-500" />
                  Consultas
                </span>
                <span className="text-sm font-bold">
                  {
                    notifications.filter((n) => n.type === "consultation")
                      .length
                  }
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2 text-red-500" />
                  Vencimientos
                </span>
                <span className="text-sm font-bold">
                  {notifications.filter((n) => n.type === "deadline").length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Configuración</CardTitle>
            <CardDescription>
              Personaliza tus preferencias de notificaciones
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  Notificaciones por email
                </span>
                <input
                  type="checkbox"
                  checked={prefs.email_notifications}
                  onChange={() => toggleQuickPref("email_notifications")}
                  className="h-4 w-4 rounded border-gray-300 text-legal-blue focus:ring-legal-blue cursor-pointer"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Notificaciones push</span>
                <input
                  type="checkbox"
                  checked={prefs.push_notifications}
                  onChange={() => toggleQuickPref("push_notifications")}
                  className="h-4 w-4 rounded border-gray-300 text-legal-blue focus:ring-legal-blue cursor-pointer"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Actualizaciones de Casos</span>
                <input
                  type="checkbox"
                  checked={prefs.case_updates}
                  onChange={() => toggleQuickPref("case_updates")}
                  className="h-4 w-4 rounded border-gray-300 text-legal-blue focus:ring-legal-blue cursor-pointer"
                />
              </div>
              <div className="pt-2 border-t border-gray-100">
                <Link to="/dashboard/settings" className="text-xs text-legal-blue hover:underline font-medium">
                  Ver configuración avanzada en Ajustes →
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Notifications;
